import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import "./App.css";
import { useAuth } from "./lib/auth";
import { api, absUrl } from "./lib/api";
import { setFavicon } from "./lib/favicon";

import PublicLayout from "./components/public/PublicLayout";
import Home from "./pages/public/Home";
import About from "./pages/public/About";
import Services from "./pages/public/Services";
import Menus from "./pages/public/Menus";
import Packages from "./pages/public/Packages";
import Gallery from "./pages/public/Gallery";
import SpecialSetups from "./pages/public/SpecialSetups";
import Testimonials from "./pages/public/Testimonials";
import FAQPage from "./pages/public/FAQPage";
import Contact from "./pages/public/Contact";
import Blog from "./pages/public/Blog";

import AdminLogin from "./pages/admin/Login";
import AdminLayout from "./components/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import AdminSettings from "./pages/admin/Settings";
import AdminPackages from "./pages/admin/Packages";
import AdminMenus from "./pages/admin/Menus";
import AdminServices from "./pages/admin/Services";
import AdminGallery from "./pages/admin/Gallery";
import AdminTestimonials from "./pages/admin/Testimonials";
import AdminFAQ from "./pages/admin/FAQ";
import AdminBlog from "./pages/admin/Blog";
import AdminLeads from "./pages/admin/Leads";
import AdminCustomers from "./pages/admin/Customers";
import AdminQuotations from "./pages/admin/Quotations";
import AdminInvoices from "./pages/admin/Invoices";
import AdminMedia from "./pages/admin/Media";
import ChangePassword from "./pages/admin/ChangePassword";

const Protected = ({ children }) => {
  const { token } = useAuth();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

export default function App() {
  const { token, setAuth, logout } = useAuth();
  // True only while we still need to confirm an existing token with the
  // server. Previously this flag existed but was never actually used to
  // block rendering, so a stale/expired token would still flash the admin
  // panel open for a moment before bouncing back to login.
  const [verifying, setVerifying] = useState(!!token);

  useEffect(() => {
    if (!token) { setVerifying(false); return; }
    api.get("/auth/me")
      .then((r) => setAuth(token, r.data))
      .catch(() => logout())
      .finally(() => setVerifying(false));
    // eslint-disable-next-line
  }, []);

  // Use the admin-uploaded logo (Site Settings → Logo) as the browser tab
  // favicon across the entire app — public site and admin panel alike.
  useEffect(() => {
    api.get("/public/site").then((r) => {
      if (r.data?.logo_url) setFavicon(absUrl(r.data.logo_url));
    }).catch(() => {});
  }, []);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900 text-gold text-sm tracking-[0.2em] uppercase">
        Verifying session…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { background: "#0A1128", color: "#FAF9F6", border: "1px solid #D4AF37" } }} />
      <Routes>
        {/* Public site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/menus" element={<Menus />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/special-setups" element={<SpecialSetups />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
        </Route>

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<Protected><AdminLayout /></Protected>}>
          <Route path="/admin" element={<Dashboard />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/packages" element={<AdminPackages />} />
          <Route path="/admin/menus" element={<AdminMenus />} />
          <Route path="/admin/services" element={<AdminServices />} />
          <Route path="/admin/gallery" element={<AdminGallery />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/faq" element={<AdminFAQ />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/quotations" element={<AdminQuotations />} />
          <Route path="/admin/invoices" element={<AdminInvoices />} />
          <Route path="/admin/media" element={<AdminMedia />} />
          <Route path="/admin/change-password" element={<ChangePassword />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}